import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  CarSimple,
  ArrowLeft,
  ArrowRight,
  Users,
  Suitcase,
  CheckCircle,
  Warning,
  ArrowsClockwise,
  ShieldCheck,
  Clock,
  Airplane,
  ArrowCounterClockwise,
  PencilSimple,
  X,
} from '@phosphor-icons/react';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';
import { CurrencySelector } from '../components/CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { TravelpayoutsWidget } from '../components/TravelpayoutsWidget';
import { kiwitaxiSearchWidgetSrc, tourCityFor } from '../config/travelpayouts';
import { TravelExtrasCards } from '../components/TravelExtras';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TALIXO_ENABLED      = process.env.REACT_APP_TALIXO_ENABLED === 'true';
const MYTRANSFERS_ENABLED = process.env.REACT_APP_MYTRANSFERS_ENABLED === 'true';

const CAR_CLASS_IMAGES = {
  standard:        'https://iway.io/images/new-template/car-classes-slider/standard.webp',
  comfort:         'https://iway.io/images/new-template/car-classes-slider/comfort.webp',
  'business light':'https://iway.io/images/new-template/car-classes-slider/business-lite.webp',
  'business lite': 'https://iway.io/images/new-template/car-classes-slider/business-lite.webp',
  business:        'https://iway.io/images/new-template/car-classes-slider/business.webp',
  luxury:          'https://iway.io/images/new-template/car-classes-slider/luxury.webp',
  minivan:         'https://iway.io/images/new-template/car-classes-slider/minivan.webp',
  'minivan vip':   'https://iway.io/images/new-template/car-classes-slider/minivan-vip.webp',
  suv:             'https://iway.io/images/new-template/car-classes-slider/suv.webp',
  minibus:         'https://iway.io/images/new-template/car-classes-slider/minibus.webp',
};

function getCarImage(title = '') {
  const k = title.toLowerCase();
  if (CAR_CLASS_IMAGES[k]) return CAR_CLASS_IMAGES[k];
  for (const key of Object.keys(CAR_CLASS_IMAGES)) {
    if (k.includes(key) || key.includes(k)) return CAR_CLASS_IMAGES[key];
  }
  return CAR_CLASS_IMAGES.standard;
}

function formatDuration(seconds) {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `~${h}h${m > 0 ? ` ${m}m` : ''}` : `~${m} min`;
}

function currencySymbol(c = 'GBP') {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$';
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

const USEFUL_SERVICES = ['meeting', 'meet', 'greet', 'waiting', 'cancellation', 'flight', 'free'];
function filterServices(services = []) {
  return services.filter(s => {
    const t = (s.title || '').toLowerCase();
    return USEFUL_SERVICES.some(k => t.includes(k));
  }).slice(0, 3);
}

// Shown only when our own suppliers return nothing, so it never competes with
// a bookable result.
function PartnerTransferSearch({ currency }) {
  useEffect(() => {
    trackEvent('partner_search_shown', { partner: 'kiwitaxi' });
  }, []);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm text-left" data-testid="partner-transfer-search">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">More transfer options</p>
      <h2 className="text-base font-semibold text-slate-900 mb-1">Search our partner network</h2>
      <p className="text-sm text-slate-500 mb-4">
        Enter your route below to see fixed-price transfers from our partner network.
      </p>
      <TravelpayoutsWidget src={kiwitaxiSearchWidgetSrc(currency)} dataTestId="kiwitaxi-widget" />
    </div>
  );
}

export default function IWayResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currency } = useCurrency();

  // Mutable search — starts from nav state, updated when user edits the search
  const [currentSearch, setCurrentSearch] = useState(location.state);
  const currentSearchRef = useRef(location.state);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  // Edit panel
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(location.state || {});
  const [editTimeError, setEditTimeError] = useState('');

  // Keep ref in sync with state
  useEffect(() => {
    currentSearchRef.current = currentSearch;
  }, [currentSearch]);

  // Re-fetch on currency change (or initial mount)
  useEffect(() => {
    if (!currentSearchRef.current) { navigate('/'); return; }
    doSearch(currentSearchRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const doSearch = async (search) => {
    setLoading(true);
    setError('');
    try {
      const iwayPromise = axios.get(`${API}/iway/search`, {
        params: {
          pickup:   search.pickup_location,
          dropoff:  search.dropoff_location,
          currency: currency || search.currency || 'GBP',
          lang:     'en',
        }
      });

      const talixoPromise = TALIXO_ENABLED
        ? axios.get(`${API}/talixo/search`, {
            params: {
              pickup:     search.pickup_location,
              dropoff:    search.dropoff_location,
              date:       search.pickup_date,
              time:       search.pickup_time,
              passengers: search.passengers || 1,
              luggage:    search.luggage || 1,
              currency:   currency || search.currency || 'GBP',
            }
          }).catch(err => {
            console.warn('[PT] Talixo search failed (non-fatal):', err?.response?.data?.detail || err.message);
            return null;
          })
        : Promise.resolve(null);

      const mytransfersPromise = MYTRANSFERS_ENABLED
        ? axios.get(`${API}/mytransfers/search`, {
            params: {
              pickup:     search.pickup_location,
              dropoff:    search.dropoff_location,
              date:       search.pickup_date,
              time:       search.pickup_time,
              passengers: search.passengers || 1,
              children:   0,
            }
          }).catch(err => {
            console.warn('[PT] MyTransfers search failed (non-fatal):', err?.response?.data?.detail || err.message);
            return null;
          })
        : Promise.resolve(null);

      const [iwayRes, talixoRes, mytransfersRes] = await Promise.all([iwayPromise, talixoPromise, mytransfersPromise]);

      const iwayVehicles = (iwayRes.data.vehicles || []).map(v => ({ ...v, supplier: 'iway' }));
      const talixoVehicles = talixoRes ? (talixoRes.data?.vehicles || []) : [];
      const mytransfersVehicles = mytransfersRes ? (mytransfersRes.data?.vehicles || []) : [];

      const allVehicles = [
        ...iwayVehicles.sort((a, b) => (a.price || 0) - (b.price || 0)),
        ...talixoVehicles.sort((a, b) => (a.price || 0) - (b.price || 0)),
        ...mytransfersVehicles.sort((a, b) => (a.price || 0) - (b.price || 0)),
      ];

      setResults({ ...iwayRes.data, vehicles: allVehicles });
      trackEvent('results_viewed', {
        pickup:            search.pickup_location,
        dropoff:           search.dropoff_location,
        results_count:     allVehicles.length,
        iway_count:        iwayVehicles.length,
        talixo_count:      talixoVehicles.length,
        mytransfers_count: mytransfersVehicles.length,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to find transfers for this route. Please try a different location.');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = (vehicle) => {
    trackEvent('vehicle_selected', {
      vehicle_class: vehicle.car_class?.title || 'Standard',
      price: vehicle.price,
      currency: vehicle.currency || 'GBP',
    });
    const bookingState = { vehicle, fromPlace: results.from_place, toPlace: results.to_place, searchData: currentSearch };
    try { sessionStorage.setItem('pt_booking_state', JSON.stringify(bookingState)); } catch {}
    navigate('/passenger-details', { state: bookingState });
  };

  // ── Edit form handlers ──
  const openEdit = () => {
    const base = { ...currentSearch };
    if (!base.pickup_time) base.pickup_time = '09:00';
    setEditForm(base);
    setEditTimeError('');
    setEditMode(true);
  };

  const handleEditInput = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  // Validate 24h advance rule whenever date or time changes in edit form
  useEffect(() => {
    if (!editMode) return;
    if (editForm.pickup_date && editForm.pickup_time) {
      const pickupDt = new Date(`${editForm.pickup_date}T${editForm.pickup_time}`);
      const minDt = new Date(Date.now() + 24 * 3600 * 1000);
      setEditTimeError(pickupDt < minDt ? 'Pick-up must be at least 24 hours in advance.' : '');
    } else {
      setEditTimeError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editForm.pickup_date, editForm.pickup_time, editMode]);

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (editTimeError) return;
    const newSearch = { ...editForm };
    currentSearchRef.current = newSearch;
    setCurrentSearch(newSearch);
    setEditMode(false);
    doSearch(newSearch);
  };

  if (!currentSearch) return null;

  return (
    <div className="min-h-screen bg-[#f8f8f6] flex flex-col">

      {/* ── Navigation ── */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <CarSimple size={28} weight="fill" className="text-[#1a3a6e]" />
            <span className="font-['Playfair_Display'] text-lg font-semibold text-slate-900">Planet Transfers</span>
          </Link>
          <div className="flex items-center gap-3">
            <CurrencySelector />
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
              data-testid="modify-search-btn"
            >
              <ArrowLeft size={16} />
              Modify Search
            </button>
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10 lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8 lg:items-start">
      <div className="min-w-0">

        {/* ── Route Summary + Edit Panel ── */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8 shadow-sm">

          {/* Header row */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Your Transfer</p>
            {!editMode && (
              <button
                onClick={openEdit}
                className="flex items-center gap-1.5 text-xs text-[#0071c2] hover:text-[#005a9a] font-medium transition-colors"
                data-testid="edit-search-btn"
              >
                <PencilSimple size={13} weight="bold" />
                Edit Search
              </button>
            )}
          </div>

          {/* Current search summary (always visible) */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">From</p>
                <p className="font-semibold text-slate-900 text-sm truncate" data-testid="summary-pickup">{currentSearch.pickup_location}</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-slate-300 flex-shrink-0 hidden sm:block" />
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-700 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">To</p>
                <p className="font-semibold text-slate-900 text-sm truncate" data-testid="summary-dropoff">{currentSearch.dropoff_location}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 flex-shrink-0 sm:border-l sm:border-slate-100 sm:pl-4">
              <span className="flex items-center gap-1"><Users size={13} />{currentSearch.passengers} pax</span>
              <span>{formatDate(currentSearch.pickup_date)}</span>
              <span>{currentSearch.pickup_time}</span>
            </div>
          </div>

          {/* ── Inline Edit Form ── */}
          {editMode && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="edit-search-panel">
              <form onSubmit={handleEditSubmit} className="space-y-3">

                {/* Pickup / Dropoff */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">From</label>
                    <AddressAutocomplete
                      name="pickup_location"
                      value={editForm.pickup_location || ''}
                      onChange={handleEditInput}
                      placeholder="Pickup location"
                      dataTestId="edit-pickup-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">To</label>
                    <AddressAutocomplete
                      name="dropoff_location"
                      value={editForm.dropoff_location || ''}
                      onChange={handleEditInput}
                      placeholder="Dropoff location"
                      dataTestId="edit-dropoff-input"
                    />
                  </div>
                </div>

                {/* Date / Time / Passengers */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Date</label>
                    <input
                      type="date"
                      name="pickup_date"
                      value={editForm.pickup_date || ''}
                      onChange={handleEditInput}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="input-field text-sm"
                      data-testid="edit-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Time (24h)</label>
                    <div className="flex items-center gap-1.5 input-field h-[42px]">
                      <select
                        value={(editForm.pickup_time || '09:00').split(':')[0] || '09'}
                        onChange={e => {
                          const mins = (editForm.pickup_time || '09:00').split(':')[1] || '00';
                          setEditForm(p => ({ ...p, pickup_time: `${e.target.value}:${mins}` }));
                        }}
                        className="bg-transparent border-none outline-none text-sm font-medium text-slate-900 cursor-pointer"
                        data-testid="edit-time-hour"
                      >
                        {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <span className="text-slate-400 font-medium">:</span>
                      <select
                        value={(editForm.pickup_time || '09:00').split(':')[1] || '00'}
                        onChange={e => {
                          const hrs = (editForm.pickup_time || '09:00').split(':')[0] || '09';
                          setEditForm(p => ({ ...p, pickup_time: `${hrs}:${e.target.value}` }));
                        }}
                        className="bg-transparent border-none outline-none text-sm font-medium text-slate-900 cursor-pointer"
                        data-testid="edit-time-minute"
                      >
                        {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Passengers</label>
                    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden h-[42px]">
                      <button
                        type="button"
                        onClick={() => setEditForm(p => ({ ...p, passengers: Math.max(1, (p.passengers || 1) - 1) }))}
                        className="px-3 h-full text-slate-500 hover:bg-slate-50 transition-colors font-medium text-lg leading-none"
                        data-testid="edit-pax-minus"
                      >−</button>
                      <span className="flex-1 text-center text-sm font-semibold text-slate-900" data-testid="edit-pax-count">
                        {editForm.passengers || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditForm(p => ({ ...p, passengers: Math.min(16, (p.passengers || 1) + 1) }))}
                        className="px-3 h-full text-slate-500 hover:bg-slate-50 transition-colors font-medium text-lg leading-none"
                        data-testid="edit-pax-plus"
                      >+</button>
                    </div>
                  </div>
                </div>

                {/* 24h validation error */}
                {editTimeError && (
                  <p className="text-xs text-red-600 flex items-center gap-1.5" data-testid="edit-time-error">
                    <Warning size={13} className="flex-shrink-0" />{editTimeError}
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={!!editTimeError || !editForm.pickup_location || !editForm.dropoff_location || !editForm.pickup_date}
                    className="btn-gold py-2.5 px-5 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="edit-search-submit-btn"
                  >
                    <ArrowsClockwise size={15} />
                    Update Search
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg transition-colors"
                    data-testid="edit-search-cancel-btn"
                  >
                    <X size={14} />
                    Cancel
                  </button>
                </div>

              </form>
            </div>
          )}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="iway-loading">
            <div className="w-10 h-10 border-[3px] border-[#1a3a6e] border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-700 font-medium text-sm">Searching available vehicles…</p>
          </div>
        )}

        {/* ── Error ── */}
        {!loading && error && (
          <div className="text-center py-16" data-testid="iway-error">
            <div className="w-14 h-14 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={28} className="text-amber-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">No results found</h2>
            <p className="text-slate-500 text-sm max-w-sm mx-auto mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => doSearch(currentSearch)}
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
                data-testid="retry-search-btn"
              >
                <ArrowsClockwise size={16} /> Try Again
              </button>
              <button
                onClick={() => {
                  const qs = new URLSearchParams({
                    from: currentSearch?.pickup_location || '',
                    to:   currentSearch?.dropoff_location || '',
                  }).toString();
                  navigate(`/quote?${qs}`);
                }}
                className="flex items-center justify-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Request a Quote
              </button>
            </div>
            <div className="mt-10">
              <PartnerTransferSearch currency={currency} />
            </div>
          </div>
        )}

        {/* ── Results ── */}
        {!loading && results && !error && (
          <>
            <div className="mb-5">
              <h1 className="font-['Playfair_Display'] text-2xl font-semibold text-slate-900" data-testid="results-heading">
                Available Transfers
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {results.vehicles.length} vehicle{results.vehicles.length !== 1 ? 's' : ''} available — live availability
              </p>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="results-trust-badges">
              {[
                { icon: ShieldCheck,  label: 'Secure booking' },
                { icon: CheckCircle,  label: 'Fixed price, no hidden fees' },
                { icon: CheckCircle,  label: '24/7 WhatsApp support' },
                { icon: CheckCircle,  label: 'Instant confirmation' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                  <Icon size={14} weight="fill" className="text-[#1a3a6e] flex-shrink-0" />
                  <span className="text-xs text-slate-600 leading-tight">{label}</span>
                </div>
              ))}
            </div>

            <div className="space-y-4" data-testid="vehicle-results">
              {results.vehicles.map((vehicle, idx) => {
                const cc = vehicle.car_class || {};
                const title = cc.title || 'Standard';
                const models = (cc.models || []).join(', ');
                const capacity = cc.capacity || vehicle.capacity;
                const luggage = cc.luggage_capacity;
                const price = vehicle.price;
                const sym = currencySymbol(vehicle.currency);
                const duration = formatDuration(vehicle.travel_time);
                const distance = vehicle.distance ? `${Math.round(vehicle.distance)} km` : null;
                const services = filterServices(vehicle.class_services);
                const overCapacity = capacity && currentSearch?.passengers > capacity;

                return (
                  <div
                    key={vehicle.price_id || idx}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#1a3a6e]/50 hover:shadow-md transition-all duration-200"
                    data-testid={`vehicle-card-${idx}`}
                  >
                    <div className="flex flex-col sm:flex-row">

                      {/* Vehicle image */}
                      <div className="sm:w-40 bg-slate-50 flex items-center justify-center p-5 flex-shrink-0">
                        <img
                          src={getCarImage(title)}
                          alt={`${title} transfer`}
                          className="h-20 w-auto object-contain"
                          onError={(e) => { e.target.src = CAR_CLASS_IMAGES.standard; }}
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between gap-4">

                          {/* Left: name + specs */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h2 className="font-semibold text-slate-900 text-base">{title}</h2>
                              {vehicle.supplier === 'talixo' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                  Talixo
                                </span>
                              )}
                              {vehicle.supplier === 'mytransfers' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                                  MyTransfers
                                </span>
                              )}
                              {/* iWay badge intentionally hidden — never show supplier name */}
                            </div>
                            {vehicle.car_model && vehicle.supplier === 'talixo' && (
                              <p className="text-xs text-slate-400 mt-0.5">{vehicle.car_model}</p>
                            )}
                            {models && vehicle.supplier !== 'talixo' && vehicle.supplier !== 'mytransfers' && (
                              <p className="text-xs text-slate-400 mt-0.5">{models} or similar</p>
                            )}

                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
                              {capacity && (
                                <span className="flex items-center gap-1"><Users size={12} className="text-slate-400" />Up to {capacity} pax</span>
                              )}
                              {luggage != null && (
                                <span className="flex items-center gap-1"><Suitcase size={12} className="text-slate-400" />{luggage} bags</span>
                              )}
                              {duration && (
                                <span className="flex items-center gap-1"><Clock size={12} className="text-slate-400" />{duration}</span>
                              )}
                              {distance && <span className="text-slate-400">{distance}</span>}
                            </div>

                            {services.length > 0 && (
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
                                {services.map(s => {
                                  const isWaiting = (s.title || '').toLowerCase().includes('wait');
                                  const label = isWaiting && s.value
                                    ? `${s.value} min free waiting`
                                    : `${s.title}${s.value ? ` (${s.value} min)` : ''}`;
                                  return (
                                    <span key={s.id} className="flex items-center gap-1 text-[11px] text-green-700">
                                      <CheckCircle size={11} weight="fill" className="text-green-500 flex-shrink-0" />
                                      {label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Right: price + CTA */}
                          <div className="flex flex-col items-end gap-3 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Total price</p>
                              <p className="text-xl font-bold text-slate-900 leading-tight">{sym}{typeof price === 'number' ? price.toFixed(2) : price}</p>
                              <p className="text-[10px] text-slate-400">One way · all taxes included</p>
                            </div>
                            {overCapacity && (
                              <p className="text-[11px] text-amber-600 text-right flex items-center gap-1">
                                <Warning size={11} className="flex-shrink-0" />
                                Max {capacity} pax
                              </p>
                            )}
                            <button
                              onClick={() => handleBook(vehicle)}
                              disabled={overCapacity}
                              className={`btn-gold py-2.5 px-5 text-sm inline-flex items-center gap-2 whitespace-nowrap ${overCapacity ? 'opacity-40 cursor-not-allowed' : ''}`}
                              data-testid={`book-vehicle-btn-${idx}`}
                            >
                              Book Now
                              <ArrowRight size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {results.vehicles.length === 0 && (
              <PartnerTransferSearch currency={currency} />
            )}

            {/* Disclaimer */}
            <p className="text-center text-xs text-slate-400 mt-8 leading-relaxed">
              * Estimated price based on route and demand. Final price confirmed after booking.
              Free cancellation may apply — see booking terms.
            </p>

            {/* Minimal trust row */}
            <div className="flex flex-wrap justify-center gap-5 mt-4 mb-2">
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <ShieldCheck size={13} className="text-green-500" />Secure payment
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <CheckCircle size={13} className="text-blue-400" />Instant confirmation
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <Clock size={13} className="text-amber-400" />Free waiting time
              </span>
            </div>
          </>
        )}
      </div>

        {/* ── Cross-sells for the destination ── */}
        <aside className="mt-10 lg:mt-0 lg:sticky lg:top-24" data-testid="results-sidebar">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Complete your trip</p>
          <TravelExtrasCards
            city={tourCityFor(currentSearch.dropoff_location) || tourCityFor(currentSearch.pickup_location)}
            currency={currency}
            placement="results_sidebar"
          />
        </aside>
      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-slate-100 mt-auto">
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CarSimple size={20} weight="fill" className="text-[#1a3a6e]" />
            <span className="font-['Playfair_Display'] font-semibold text-slate-900 text-sm">Planet Transfers</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <Link to="/terms-and-conditions" className="hover:text-slate-600 transition-colors">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-slate-600 transition-colors">Privacy</Link>
            <span>© {new Date().getFullYear()} Planet Transfers</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
